import {
  ZENDESK_COMMENT,
  ZENDESK_SUBJECT,
  ZENDESK_PRIORITY,
  ZENDESK_TYPE,
  ZENDESK_EXTERNAL_ID,
  ZENDESK_RAW_SUBJECT,
  ZENDESK_STATUS,
  ZENDESK_RECIPIENT,
  ZENDESK_REQUESTER_ID,
  ZENDESK_REQUESTER,
  ZENDESK_SUBMITTER_ID,
  ZENDESK_ASSIGNEE_ID,
  ZENDESK_ASSIGNEE_EMAIL,
  ZENDESK_ORGANIZATION_ID,
  ZENDESK_GROUP_ID,
  ZENDESK_PROBLEM_ID,
  ZENDESK_DUE_AT,
  ZENDESK_VIA_FOLLOWUP_SOURCE_ID,
  ZENDESK_TICKET_FORM_ID,
  ZENDESK_BRAND_ID,
  ZENDESK_MACRO_ID,
  ZENDESK_UPDATED_STAMP,
  ZENDESK_SAFE_UPDATE,
  ZENDESK_COLLABORATOR_IDS,
  ZENDESK_ATTRIBUTE_VALUE_IDS,
  ZENDESK_COLLABORATORS,
  ZENDESK_FOLLOWERS,
  ZENDESK_EMAIL_CCS,
  ZENDESK_TAGS,
  ZENDESK_MACRO_IDS,
  ZENDESK_CUSTOM_FIELDS,
  ZENDESK_METADATA,
  ZENDESK_VIA,
  ZENDESK_COLLABORATOR_ID_VALUE,
  ZENDESK_COMMENT_BODY,
  ZENDESK_COMMENT_HTML_BODY,
  ZENDESK_COMMENT_PUBLIC,
  ZENDESK_COMMENT_AUTHOR_ID,
  ZENDESK_COMMENT_UPLOADS,
} from "@avenueops/shared-types";

interface Props {
  fullFieldName: string;
}

export function getZendeskFieldDescription(fullFieldName: string): JSX.Element | null {
  switch (fullFieldName) {
    case ZENDESK_COMMENT:
      return (
        <span>
          An object that adds a comment to the ticket. See the Zendesk{" "}
          <strong>
            <a href="https://developer.zendesk.com/api-reference/ticketing/tickets/ticket_comments/">ticket comments</a>
          </strong>{" "}
          docs to learn more
        </span>
      );
    case ZENDESK_SUBJECT:
      return <span>The value of the subject field for this ticket</span>;
    case ZENDESK_PRIORITY:
      return (
        <span>
          The urgency with which the ticket should be addressed. Allowed values are <code>urgent</code>, <code>high</code>, <code>normal</code>, or{" "}
          <code>low</code>.
        </span>
      );
    case ZENDESK_TYPE:
      return (
        <span>
          The type of this ticket. Allowed values are <code>problem</code>, <code>incident</code>, <code>question</code>, or <code>task</code>.
        </span>
      );
    case ZENDESK_EXTERNAL_ID:
      return <span>An id you can use to link Zendesk Support tickets to local records</span>;
    case ZENDESK_RAW_SUBJECT:
      return (
        <span>
          The dynamic content placeholder, if present, or the "subject" value, if not. See{" "}
          <strong>
            <a href="https://developer.zendesk.com/api-reference/ticketing/ticket-management/dynamic_content/">Dynamic Content Items</a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_STATUS:
      return (
        <span>
          The state of the ticket. If your account has activated custom ticket statuses, this is the ticket's status category. See custom ticket statuses.
          Allowed values are <code>new</code>, <code>open</code>, <code>pending</code>, <code>hold</code>, <code>solved</code>, or <code>closed</code>.
        </span>
      );
    case ZENDESK_RECIPIENT:
      return <span>The original recipient e-mail address of the ticket. Notification emails for the ticket are sent from this address</span>;
    case ZENDESK_REQUESTER_ID:
      return <span>The user who requested this ticket</span>;
    case ZENDESK_REQUESTER:
      return (
        <span>
          See{" "}
          <strong>
            <a href="https://developer.zendesk.com/documentation/ticketing/managing-tickets/creating-and-updating-tickets/#creating-a-ticket-with-a-new-requester">
              Creating a ticket with a new requester
            </a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_SUBMITTER_ID:
      return <span>The user who submitted the ticket. The submitter always becomes the author of the first comment on the ticket</span>;
    case ZENDESK_ASSIGNEE_ID:
      return <span>The agent currently assigned to the ticket</span>;
    case ZENDESK_ASSIGNEE_EMAIL:
      return <span>The email address of the agent to assign the ticket to</span>;
    case ZENDESK_ORGANIZATION_ID:
      return <span>The organization of the requester. You can only specify the ID of an organization associated with the requester</span>;
    case ZENDESK_GROUP_ID:
      return <span>The group this ticket is assigned to</span>;
    case ZENDESK_PROBLEM_ID:
      return (
        <span>
          For tickets of type <code>incident</code>, /the ID of the problem the incident is linked to
        </span>
      );
    case ZENDESK_DUE_AT:
      return (
        <span>
          If this is a ticket of type <code>task</code> it has a due date. Due date format uses ISO 8601 format.
        </span>
      );
    case ZENDESK_VIA_FOLLOWUP_SOURCE_ID:
      return (
        <span>
          The id of a closed ticket when creating a follow-up ticket. See{" "}
          <strong>
            <a href="https://developer.zendesk.com/documentation/ticketing/managing-tickets/creating-and-updating-tickets/#creating-a-follow-up-ticket">
              Creating a follow-up ticket
            </a>{" "}
            for more info{" "}
          </strong>
        </span>
      );
    case ZENDESK_TICKET_FORM_ID:
      return <span>Enterprise only. The id of the ticket form to render for the ticket</span>;
    case ZENDESK_BRAND_ID:
      return <span>Enterprise only. The id of the brand this ticket is associated with</span>;
    case ZENDESK_MACRO_ID:
      return <span>A macro ID to be recorded in the ticket audit</span>;
    case ZENDESK_UPDATED_STAMP:
      return (
        <span>
          Datetime of last update received from API. See the <code>safe_update</code> property
        </span>
      );
    case ZENDESK_SAFE_UPDATE:
      return (
        <span>
          Prevents updates with outdated ticket data (<code>updated_stamp</code> property required when true).
        </span>
      );
    case ZENDESK_COLLABORATOR_IDS:
      return <span>The ids of users currently CC'ed on the ticket</span>;
    case ZENDESK_ATTRIBUTE_VALUE_IDS:
      return <span>An array of the IDs of attribute values to be associated with the ticket</span>;
    case ZENDESK_COLLABORATORS:
      return (
        <span>
          Users to add as cc's when creating a ticket. See{" "}
          <strong>
            <a href="https://developer.zendesk.com/documentation/ticketing/managing-tickets/creating-and-updating-tickets/#setting-collaborators">
              Setting Collaborators
            </a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_FOLLOWERS:
      return (
        <span>
          An array of objects that represent agent followers to add or delete from the ticket. See{" "}
          <strong>
            <a href="https://developer.zendesk.com/documentation/ticketing/managing-tickets/creating-and-updating-tickets/#setting-followers">
              Setting followers
            </a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_EMAIL_CCS:
      return (
        <span>
          An array of objects that represent agent or end users email CCs to add or delete from the ticket. See{" "}
          <strong>
            <a href="/documentation/ticketing/managing-tickets/creating-and-updating-tickets/#setting-email-ccs">Setting email CCs</a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_TAGS:
      return <span>The array of tags applied to this ticket</span>;
    case ZENDESK_MACRO_IDS:
      return <span>List of macro IDs to be recorded in the ticket audit</span>;
    case ZENDESK_CUSTOM_FIELDS:
      return (
        <span>
          Custom fields for the ticket. See{" "}
          <strong>
            <a href="/documentation/ticketing/managing-tickets/creating-and-updating-tickets#setting-custom-field-values">Setting custom field values</a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_METADATA:
      return (
        <span>
          Metadata for the audit. In the audit object, the data is specified in the custom property of the metadata object. See{" "}
          <strong>
            <a href="/documentation/ticketing/managing-tickets/creating-and-updating-tickets/#setting-metadata">Setting Metadata</a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_VIA:
      return (
        <span>
          For more information, see the{" "}
          <strong>
            <a href="/documentation/ticketing/reference-guides/via-object-reference">Via object reference</a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_COLLABORATOR_ID_VALUE:
      return <span>The ids of users currently CC'ed on the ticket</span>;
    case ZENDESK_COMMENT_BODY:
      return (
        <span>
          The comment string. See{" "}
          <strong>
            <a href="#bodies">Bodies</a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_COMMENT_HTML_BODY:
      return (
        <span>
          The comment formatted as HTML. See{" "}
          <strong>
            <a href="#bodies">Bodies</a>
          </strong>{" "}
          for more info
        </span>
      );
    case ZENDESK_COMMENT_PUBLIC:
      return (
        <span>
          true if a public comment; false if an internal note. The initial value set on ticket creation persists for any additional comment unless you change it
        </span>
      );
    case ZENDESK_COMMENT_AUTHOR_ID:
      return (
        <span>
          The id of the comment author. See <a href="#author-id">Author id</a>
        </span>
      );
    case ZENDESK_COMMENT_UPLOADS:
      return (
        <span>
          List of tokens received from <a href="/api-reference/ticketing/tickets/ticket-attachments/#upload-files">uploading files</a> for comment attachments.
          The files are attached by creating or updating tickets with the tokens. See{" "}
          <strong>
            <a href="/api-reference/ticketing/tickets/tickets/#attaching-files">Attaching files</a>
          </strong>{" "}
          in Tickets for more info
        </span>
      );
    default:
      return null;
  }
}
